import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService, getTokenClaims } from '../services/authService';
import { isAdminOrHasRBACRole, canManageRBAC } from '../utils/role';
import { CircularProgress, Box } from '@mui/material';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const verifyAccess = async () => {
      // Check authentication first
      if (!isAuthenticated || !user) {
        setAuthorized(false);
        setVerifying(false);
        return;
      }

      // Check via legacy role OR RBAC
      const hasAccess = isAdminOrHasRBACRole(user?.role) || canManageRBAC(user?.role);
      
      if (hasAccess) {
        setAuthorized(true);
        setVerifying(false);
        return;
      }

      // Also verify with API as a fallback
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const claims = getTokenClaims(token);
          const rbacRoles = authService.getUserRoles();
          const hasRBACAdmin = rbacRoles.includes('SystemAdmin') || rbacRoles.includes('Admin');
          const userIdFromClaim = claims?.nameid || claims?.userId;
          
          if (hasRBACAdmin || userIdFromClaim) {
            setAuthorized(true);
            setVerifying(false);
            return;
          }
        }
      } catch {
        // Fallback to local check only
      }
      
      setAuthorized(false);
      setVerifying(false);
    };

    verifyAccess();
  }, [isAuthenticated, user]);

  if (verifying) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!authorized) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;